import { useEffect } from 'react';

/**
 * Rows a partially-narrowed list aims to show before it stops pulling pages
 * on its own — roughly a screenful, so the user sees a full list and the
 * scroll sentinel takes over from there.
 */
export const COMPLETION_TARGET_ROWS = 40;

/**
 * How many pages a view may hold before it stops pulling them on its own. The
 * cap is what keeps this from turning a filter into a full-table scan on a
 * large org: past it the list falls back to plain infinite scroll, and the
 * caller can say so.
 */
export const COMPLETION_PAGE_BUDGET = 10;

export interface AgentListCompletionOptions {
  /**
   * Whether this view needs more than the loaded pages to be correct — a
   * filter the server does not apply, or a grouping that buckets the whole
   * list rather than a window of it.
   */
  enabled: boolean;
  /** Rows currently surviving the client-side pass. */
  matchedCount: number;
  /**
   * Rows to reach before stopping. `Infinity` for views that are only correct
   * over the complete list (bucketing by group: a section whose members are
   * all older than the loaded pages is a section that silently goes missing).
   */
  targetRows?: number;
  /**
   * Pages the list currently holds. Doubles as the budget counter: a change
   * of filters is a change of query key, which starts a fresh cache entry at
   * one page, so the budget resets itself with no state to keep in step.
   */
  pagesLoaded: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

/**
 * Keep pulling pages until a client-narrowed list is worth looking at.
 *
 * The facets the route applies (owner, status, mark, group, label) paginate
 * server-side and need none of this. The rest — repo, association kind,
 * originator, free text, time range, unread — depend on hydrated attachments,
 * per-viewer read state, or a locale-relative cutoff, so they run in memory
 * over the pages the infinite list happens to hold. That is what makes a
 * filtered list look empty on an org whose matching runs are all older than
 * page one: the filter is correct, it is just being asked about 50 rows.
 *
 * Fetching one page at a time (rather than raising the page size for
 * everyone) keeps the unfiltered list exactly as cheap as it was, and the
 * budget keeps the narrowed one from degenerating into a scan.
 *
 * Side-effecting and returns nothing, like the scroll sentinel it sits beside:
 * this only decides when to *start* a fetch. Reaching the budget is not a
 * dead end — the sidebar's sentinel still pages on scroll, so the budget
 * bounds what the list pulls unprompted, not what the user can reach.
 */
export function useAgentListCompletion({
  enabled,
  matchedCount,
  targetRows = COMPLETION_TARGET_ROWS,
  pagesLoaded,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: AgentListCompletionOptions): void {
  const wantsMore = enabled && hasNextPage && matchedCount < targetRows;
  const withinBudget = pagesLoaded < COMPLETION_PAGE_BUDGET;
  const shouldFetch = wantsMore && withinBudget && !isFetchingNextPage;

  useEffect(() => {
    if (!shouldFetch) return;
    fetchNextPage();
  }, [shouldFetch, fetchNextPage]);
}
